import { parse as parseCsv } from "papaparse";
import * as XLSX from "xlsx";

export interface ParsedData {
  data: Record<string, unknown>[];
  objectKeys: string[];
}

const MAX_FLATTEN_DEPTH = 10;

export function flattenObjectKeys(
  obj: Record<string, unknown>,
  prefix = "",
  depth = 0,
): string[] {
  const keys: string[] = [];
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (depth >= MAX_FLATTEN_DEPTH) {
      keys.push(path);
      continue;
    }
    if (Array.isArray(value)) {
      const firstItem = value.find(
        (item) => typeof item === "object" && item !== null && !Array.isArray(item),
      );
      if (firstItem) {
        keys.push(...flattenObjectKeys(firstItem as Record<string, unknown>, path, depth + 1));
      } else {
        keys.push(path);
      }
    } else if (typeof value === "object" && value !== null) {
      keys.push(...flattenObjectKeys(value as Record<string, unknown>, path, depth + 1));
    } else {
      keys.push(path);
    }
  }
  return prefix ? keys : keys.sort((a, b) => a.localeCompare(b));
}

export function parseExcelFile(file: File): Promise<ParsedData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const csvString = XLSX.utils.sheet_to_csv(worksheet);
      const result = await parseCsvFile(csvString);
      resolve(result);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
}

export function parseCsvFile(file: File | string): Promise<ParsedData> {
  return new Promise((resolve, reject) => {
    parseCsv(file, {
      header: true,
      skipEmptyLines: true,
      error: (error) => reject(error),
      complete: (results) => {
        const processedData: Record<string, unknown>[] = [];
        const data = results.data;
        if (data.length === 0) {
          reject("Fichier vide ou non valide");
          return;
        }
        for (const row of data) {
          if (typeof row !== "object" || row === null || Array.isArray(row)) continue;
          const dataRow: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(row)) {
            dataRow[normalizeStr(key)] = value;
          }
          processedData.push(dataRow);
        }
        const objectKeys = Object.keys(processedData[0] || {});

        resolve({
          data: processedData,
          objectKeys,
        });
      },
    });
  });
}

export function parseJsonFile(file: File): Promise<ParsedData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const jsonData = JSON.parse(text);

        if (typeof jsonData !== "object" || jsonData === null) {
          reject(new Error("Fichier JSON vide"));
          return;
        }

        if (Array.isArray(jsonData)) {
          const processedData: Record<string, unknown>[] = [];
          for (const row of jsonData) {
            const dataRow: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(row)) {
              dataRow[normalizeStr(key)] = value;
            }
            processedData.push(dataRow);
          }
          resolve({
            data: processedData,
            objectKeys: flattenObjectKeys(processedData[0] || {}),
          });
          return;
        }

        const dataRow: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(jsonData)) {
          dataRow[normalizeStr(key)] = value;
        }
        resolve({
          data: [dataRow],
          objectKeys: flattenObjectKeys(dataRow),
        });
        return;
      } catch (error) {
        reject(
          new Error(`Erreur JSON: ${error instanceof Error ? error.message : "Format invalide"}`),
        );
      }
    };
    reader.onerror = () => reject(new Error("Erreur lors de la lecture du fichier"));
    reader.readAsText(file);
  });
}

function normalizeStr(str: string): string {
  return (
    str
      .trim()
      .normalize("NFD")
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: not sure
      .replace(/[\u0300-\u036f]/g, "") // remove diacritics
      .replace(/[^0-9a-zA-Z]/g, "")
      .toLowerCase()
  );
}
